import { Box, Button, Slider, TextField, Typography, Tooltip } from "@mui/material";
import { useState } from "react";
import { useRecoilState } from "recoil";
import { t } from "i18next";
import { BlurLinearIcon, ColorLensIcon, ContrastIcon, LightModeIcon } from "../../icons";
import { filterSettingState } from "../../recoil/filterAtoms";

interface ImageFilterProps {
  setOpenFilterModal: React.Dispatch<React.SetStateAction<boolean>>;
  projectConfig?: any;
  setProjectConfig?: React.Dispatch<React.SetStateAction<any | null>>;
}

type TabId = "basic" | "opencv" | "colorspace";

export const filterDefault = {
  brightness: 0,
  contrast: 0,
  saturate: 0,
  blur: 0,
  opencvFilter: {
    equalizehist: { strength: 0 },
    laplacian: { strength: 0 },
    cvtcolor: { colorSpace: "" },
  },
};

function FilterRow({
  icon,
  label,
  value,
  min,
  max,
  unit,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  min: number;
  max: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  const handleInput = (raw: string) => {
    const n = raw === "" ? 0 : Number(raw);
    if (!isNaN(n)) onChange(Math.min(Math.max(n, min), max));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
        <Box
          sx={{
            color: "var(--dk-text-3, #999)",
            display: "flex",
            alignItems: "center",
            "& svg": { fontSize: "15px" },
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "var(--dk-text-2, #444)", flex: 1 }}>{label}</Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "3px",
            bgcolor: "#f2f2f2",
            border: "0.5px solid var(--dk-border, rgba(0,0,0,0.1))",
            borderRadius: "5px",
            px: "6px",
            py: "2px",
          }}
        >
          <TextField
            type="number"
            value={value}
            onChange={(e) => handleInput(e.target.value)}
            variant="standard"
            inputProps={{ min, max, style: { textAlign: "right" } }}
            sx={{
              width: "38px",
              "& .MuiInput-root": { fontSize: "12px", fontWeight: 500 },
              "& .MuiInput-root:before": { display: "none" },
              "& .MuiInput-root:after": { display: "none" },
              "& input": {
                p: 0,
                color: "var(--dk-text, #222)",
                MozAppearance: "textfield",
                "&::-webkit-inner-spin-button": { display: "none" },
              },
            }}
          />
          <Typography sx={{ fontSize: "10px", color: "var(--dk-text-3, #aaa)", lineHeight: 1 }}>{unit}</Typography>
        </Box>
      </Box>
      <Slider
        value={value}
        min={min}
        max={max}
        step={1}
        onChange={(_, v) => onChange(v as number)}
        sx={{
          mx: "2px",
          height: 3,
          padding: "8px 0",
          "& .MuiSlider-thumb": {
            width: 13,
            height: 13,
            bgcolor: "var(--dk-surface, #fff)",
            border: "1.5px solid",
            borderColor: value === 0 ? "#ccc" : "primary.main",
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            "&:hover, &.Mui-focusVisible": {
              boxShadow: "0 0 0 6px rgba(25,118,210,0.12)",
            },
          },
          "& .MuiSlider-rail": { bgcolor: "#e5e5e5", opacity: 1 },
          "& .MuiSlider-track": { height: 3 },
        }}
      />
    </Box>
  );
}

function SideTab({
  icon,
  label,
  active,
  onClick,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: string;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "3px",
        py: "9px",
        px: "6px",
        cursor: "pointer",
        borderLeft: active ? "2px solid" : "2px solid transparent",
        borderColor: active ? "primary.main" : "transparent",
        bgcolor: active ? "rgba(25,118,210,0.06)" : "transparent",
        color: active ? "primary.main" : "#999",
        transition: "all 0.12s",
        "&:hover": {
          bgcolor: active ? "rgba(25,118,210,0.06)" : "rgba(0,0,0,0.03)",
        },
        "& svg": { fontSize: "16px" },
      }}
    >
      {icon}
      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: active ? 600 : 400,
          color: "inherit",
          lineHeight: 1.2,
          textAlign: "center",
        }}
      >
        {label}
      </Typography>
      {badge && (
        <Box
          sx={{
            fontSize: "8px",
            fontWeight: 600,
            bgcolor: "rgba(25,118,210,0.12)",
            color: "primary.main",
            borderRadius: "3px",
            px: "3px",
            py: "1px",
            lineHeight: 1.3,
          }}
        >
          {badge}
        </Box>
      )}
    </Box>
  );
}

export default function ImageFilter() {
  const [activeTab, setActiveTab] = useState<TabId>("basic");
  const [filterSetting, setFilterSetting] = useRecoilState(filterSettingState);

  const basic = {
    brightness: filterSetting.brightness,
    contrast: filterSetting.contrast,
    saturate: filterSetting.saturate,
    blur: filterSetting.blur,
  };
  const opencv = filterSetting.opencvFilter;
  const colorSpace = filterSetting.opencvFilter.cvtcolor.colorSpace;

  const handleBasic = (name: keyof typeof basic, value: number) => {
    setFilterSetting((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpencv = (name: "equalizehist" | "laplacian", value: number) => {
    setFilterSetting((prev) => ({
      ...prev,
      opencvFilter: {
        ...prev.opencvFilter,
        [name]: { strength: value },
      },
    }));
  };

  const handleColorSpace = (value: string) => {
    setFilterSetting((prev) => ({
      ...prev,
      opencvFilter: {
        ...prev.opencvFilter,
        cvtcolor: { colorSpace: value },
      },
    }));
  };

  const handleReset = () => {
    setFilterSetting(filterDefault);
  };

  const handleApply = () => {
    console.log("filterSetting", filterSetting);
  };

  const basicControls = [
    {
      name: "brightness" as const,
      label: "밝기",
      icon: <LightModeIcon />,
      min: -100,
      max: 100,
      unit: "%",
    },
    {
      name: "contrast" as const,
      label: "대비",
      icon: <ContrastIcon />,
      min: -100,
      max: 100,
      unit: "%",
    },
    {
      name: "saturate" as const,
      label: "채도",
      icon: <ColorLensIcon />,
      min: 0,
      max: 200,
      unit: "%",
    },
    {
      name: "blur" as const,
      label: "흐림",
      icon: <BlurLinearIcon />,
      min: 0,
      max: 10,
      unit: "px",
    },
  ];

  const opencvControls = [
    {
      name: "equalizehist" as const,
      label: "히스토그램 평활화",
      icon: <ContrastIcon />,
      min: 0,
      max: 100,
      unit: "%",
    },
    {
      name: "laplacian" as const,
      label: "라플라시안",
      icon: <LightModeIcon />,
      min: 0,
      max: 100,
      unit: "%",
    },
  ];

  const colorSpaceOptions = [
    { value: "", label: "없음" },
    { value: "GRAY", label: "그레이스케일" },
    { value: "HSV", label: "HSV" },
    { value: "LAB", label: "LAB" },
    { value: "BGR", label: "BGR" },
    { value: "XYZ", label: "XYZ" },
    { value: "YCrCb", label: "YCrCb" },
    { value: "HLS", label: "HLS" },
    { value: "LUV", label: "LUV" },
    { value: "YUV", label: "YUV" },
  ];

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          width: "72px",
          flexShrink: 0,
          bgcolor: "var(--dk-surface-2, #fafafa)",
          borderRight: "0.5px solid var(--dk-border, rgba(0,0,0,0.08))",
          display: "flex",
          flexDirection: "column",
          py: "6px",
        }}
      >
        <SideTab
          icon={<ContrastIcon />}
          label="기본"
          active={activeTab === "basic"}
          onClick={() => setActiveTab("basic")}
        />
        <SideTab
          icon={<BlurLinearIcon />}
          label="OpenCV"
          active={activeTab === "opencv"}
          onClick={() => setActiveTab("opencv")}
        />
        <SideTab
          icon={<ColorLensIcon />}
          label="색공간"
          active={activeTab === "colorspace"}
          onClick={() => setActiveTab("colorspace")}
        />

        <Box sx={{ flex: 1 }} />
        <Tooltip title="준비 중" placement="right">
          <Box>
            <SideTab icon={<LightModeIcon />} label="커스텀" active={false} onClick={() => {}} badge="Soon" />
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: "16px",
            py: "14px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "rgba(0,0,0,0.12)",
              borderRadius: "2px",
            },
          }}
        >
          {activeTab === "basic" &&
            basicControls.map((c) => (
              <FilterRow
                key={c.name}
                icon={c.icon}
                label={c.label}
                value={basic[c.name]}
                min={c.min}
                max={c.max}
                unit={c.unit}
                onChange={(v) => handleBasic(c.name, v)}
              />
            ))}

          {activeTab === "opencv" &&
            opencvControls.map((c) => (
              <FilterRow
                key={c.name}
                icon={c.icon}
                label={c.label}
                value={opencv[c.name].strength}
                min={c.min}
                max={c.max}
                unit={c.unit}
                onChange={(v) => handleOpencv(c.name, v)}
              />
            ))}

          {activeTab === "colorspace" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Typography sx={{ fontSize: "12px", color: "var(--dk-text-3, #888)" }}>이미지의 색 채널 표현 방식을 변환합니다.</Typography>
              {colorSpaceOptions.map((opt) => (
                <Box
                  key={opt.value}
                  onClick={() => handleColorSpace(opt.value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    px: "12px",
                    py: "9px",
                    borderRadius: "7px",
                    border: "0.5px solid",
                    borderColor: colorSpace === opt.value ? "primary.main" : "rgba(0,0,0,0.09)",
                    bgcolor: colorSpace === opt.value ? "rgba(25,118,210,0.06)" : "transparent",
                    cursor: "pointer",
                    transition: "all 0.1s",
                    "&:hover": {
                      bgcolor: colorSpace === opt.value ? "rgba(25,118,210,0.06)" : "rgba(0,0,0,0.025)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 13,
                      height: 13,
                      borderRadius: "50%",
                      border: "1.5px solid",
                      borderColor: colorSpace === opt.value ? "primary.main" : "#ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {colorSpace === opt.value && (
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    sx={{ fontSize: "12px", color: "var(--dk-text, #333)", fontWeight: colorSpace === opt.value ? 500 : 400 }}
                  >
                    {opt.label}
                  </Typography>
                  {opt.value && (
                    <Typography sx={{ fontSize: "10px", color: "#bbb", ml: "auto", fontFamily: "monospace" }}>
                      {opt.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: "14px",
            py: "10px",
            borderTop: "0.5px solid var(--dk-border, rgba(0,0,0,0.08))",
            bgcolor: "var(--dk-surface-2, #fafafa)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            size="small"
            onClick={handleReset}
            sx={{
              fontSize: "11px",
              color: "var(--dk-text-3, #999)",
              minWidth: 0,
              px: "8px",
              "&:hover": { color: "var(--dk-text-2, #555)", bgcolor: "rgba(0,0,0,0.04)" },
            }}
          >
            {t("AllClear")}
          </Button>

          <Box sx={{ display: "flex", gap: "6px" }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={handleApply}
              sx={{
                fontSize: "12px",
                borderRadius: "6px",
                px: "12px",
                fontWeight: 500,
                boxShadow: "none",
              }}
            >
              {t("Apply")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
